import { useEffect } from 'react'
import { assertPlatformAccountAccess, PlatformAccountBlockedError } from '../services/platformAccountAccess'

const CHECK_INTERVAL_MS = 60_000

export interface UsePlatformAccountGuardOptions {
  userId: string | null | undefined
  onBlocked: (error: PlatformAccountBlockedError) => void
}

/**
 * Controleert periodiek of het platformaccount nog actief is.
 * JWT's kunnen kort geldig blijven na sessie-intrekking; dit vangt ban/suspend sneller op.
 */
export function usePlatformAccountGuard ({
  userId,
  onBlocked,
}: UsePlatformAccountGuardOptions): void {
  useEffect(() => {
    if (!userId) return

    let cancelled = false

    async function checkAccess (): Promise<void> {
      try {
        await assertPlatformAccountAccess()
      } catch (error) {
        if (cancelled) return
        if (error instanceof PlatformAccountBlockedError) {
          onBlocked(error)
        }
      }
    }

    void checkAccess()

    const intervalId = window.setInterval(() => {
      void checkAccess()
    }, CHECK_INTERVAL_MS)

    function handleVisibility (): void {
      if (document.visibilityState === 'visible') {
        void checkAccess()
      }
    }

    function handleFocus (): void {
      void checkAccess()
    }

    document.addEventListener('visibilitychange', handleVisibility)
    window.addEventListener('focus', handleFocus)

    return () => {
      cancelled = true
      window.clearInterval(intervalId)
      document.removeEventListener('visibilitychange', handleVisibility)
      window.removeEventListener('focus', handleFocus)
    }
  }, [userId, onBlocked])
}
